import React, { useEffect, useRef, useState } from 'react';
import notificationService, { NotificationItem } from '../../services/notificationService';
import '../../assets/styles/Workflow.css';

const POLL_INTERVAL_MS = 30000;

const timeAgo = (iso: string) => {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

const TOAST_DURATION_MS = 6000;
// Caps how many individual toasts can stack at once - a first-ever login
// after a backlog of unread notifications piled up (e.g. several days away)
// would otherwise dump every single one down the screen at once. Anything
// past the cap is folded into one "+N more" summary toast instead.
const MAX_VISIBLE_TOASTS = 3;
// Persisted (not just in-memory) so a notification only ever pops once on
// this device, but *does* still pop the first time it's ever fetched - e.g.
// someone assigned a project while logged out must still see a toast the
// moment they next log in, not just for notifications created while they
// already had the page open. Without persistence, every fresh login/page
// load would look like "first load" and silently swallow the toast.
const TOASTED_IDS_KEY = 'wf_notif_toasted_ids';
const MAX_STORED_TOASTED_IDS = 500;

const loadToastedIds = (): Set<number> => {
  try {
    const raw = localStorage.getItem(TOASTED_IDS_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
};

const saveToastedIds = (ids: Set<number>) => {
  try {
    localStorage.setItem(TOASTED_IDS_KEY, JSON.stringify(Array.from(ids).slice(-MAX_STORED_TOASTED_IDS)));
  } catch {
    // localStorage unavailable/full - toasts just won't dedupe across reloads.
  }
};

const NotificationBell: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [unread, setUnread] = useState(0);
  const [toasts, setToasts] = useState<NotificationItem[]>([]);
  const [extraCount, setExtraCount] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const toastedIds = useRef<Set<number>>(loadToastedIds());

  const load = () => {
    notificationService.list(1, 20).then((res) => {
      setItems(res.data);
      setUnread(res.unread_count);

      const fresh = res.data.filter((n) => !n.read && !toastedIds.current.has(n.id));
      if (fresh.length > 0) {
        fresh.forEach((n) => toastedIds.current.add(n.id));
        saveToastedIds(toastedIds.current);
        const visible = fresh.slice(0, MAX_VISIBLE_TOASTS);
        setToasts((prev) => [...visible, ...prev]);
        visible.forEach((n) => setTimeout(() => dismissToast(n.id), TOAST_DURATION_MS));
        const overflow = fresh.length - visible.length;
        if (overflow > 0) {
          setExtraCount((prev) => prev + overflow);
          setTimeout(() => setExtraCount((prev) => Math.max(0, prev - overflow)), TOAST_DURATION_MS);
        }
      }
    }).catch(() => {});
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  const dismissToast = (id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const onItemClick = async (item: NotificationItem) => {
    if (!item.read) {
      await notificationService.markRead(item.id);
      load();
    }
  };

  const markAllRead = async () => {
    await notificationService.markAllRead();
    load();
  };

  const onToastClick = (n: NotificationItem) => {
    onItemClick(n);
    dismissToast(n.id);
  };

  const onExtraClick = () => {
    setExtraCount(0);
    setOpen(true);
  };

  return (
    <div style={{ position: 'relative' }} ref={containerRef}>
      {(toasts.length > 0 || extraCount > 0) && (
        <div className="wf-notif-toast-stack">
          {toasts.map((n) => (
            <div key={n.id} className="wf-notif-toast" onClick={() => onToastClick(n)}>
              <div className="wf-notif-toast-head">
                <h4>{n.title}</h4>
                <button
                  className="wf-notif-toast-close"
                  aria-label="Dismiss"
                  onClick={(e) => { e.stopPropagation(); dismissToast(n.id); }}
                >
                  ✕
                </button>
              </div>
              {n.message && <p>{n.message}</p>}
            </div>
          ))}
          {extraCount > 0 && (
            <div className="wf-notif-toast wf-notif-toast-extra" onClick={onExtraClick}>
              <div className="wf-notif-toast-head">
                <h4>+{extraCount} more notification{extraCount > 1 ? 's' : ''}</h4>
                <button
                  className="wf-notif-toast-close"
                  aria-label="Dismiss"
                  onClick={(e) => { e.stopPropagation(); setExtraCount(0); }}
                >
                  ✕
                </button>
              </div>
              <p>Click to view all</p>
            </div>
          )}
        </div>
      )}
      <button className="wf-notif-bell" onClick={() => setOpen(!open)} aria-label="Notifications">
        🔔{unread > 0 && <span className="wf-notif-badge">{unread}</span>}
      </button>
      {open && (
        <div className="wf-notif-dropdown">
          <div className="wf-notif-head">
            <strong style={{ fontSize: 13 }}>Notifications</strong>
            {unread > 0 && (
              <span style={{ fontSize: 11.5, color: '#2E9FD9', cursor: 'pointer' }} onClick={markAllRead}>
                Mark all as read
              </span>
            )}
          </div>
          {items.length === 0 ? (
            <div className="wf-notif-empty">No notifications yet.</div>
          ) : (
            items.map((n) => (
              <div key={n.id} className={`wf-notif-item ${n.read ? '' : 'unread'}`} onClick={() => onItemClick(n)}>
                <h4>{n.title}</h4>
                <p>{n.message}</p>
                <span style={{ color: '#9ca3af', fontSize: 10.5 }}>{timeAgo(n.created_at)}</span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
